import { useMemo } from 'react';
import { toActionBarBindingId, DRAW_SKILL_ID } from '@sorrem/shared';
import { useActionBarTargets } from './use-action-bar-targets';
import { useCallout } from './use-callout';
import { formatBindingLabel, useInputBindings } from './use-input-bindings';

/** Where the Draw callout points, what it says, and its Close / Don't show again. */
export interface DrawActionCallout {
    index: number;
    label: string;
    onClose: () => void;
    onDismiss: () => void;
}

/**
 * The Draw callout for the loadout: on whichever action-bar slot holds Draw
 * (the last one unless the player moved it), naming its key, until the
 * account first casts it or opts out. Null while closed, or when no slot
 * holds Draw.
 */
export function useDrawActionCallout(): DrawActionCallout | null {
    const { isOpen, close, dismiss } = useCallout('draw');
    const { targets } = useActionBarTargets();
    const { bindings } = useInputBindings();
    return useMemo((): DrawActionCallout | null => {
        if (!isOpen) {
            return null;
        }
        const index = targets.findIndex((target) => target?.kind === 'skill' && target.skillId === DRAW_SKILL_ID);
        const id = toActionBarBindingId(index);
        if (id === undefined) {
            return null;
        }
        return {
            index,
            label: `Draw (${formatBindingLabel(bindings, id)}) is your lifeline: it's how you recover essence. Vital to every Crucible run.`,
            onClose: close,
            onDismiss: dismiss,
        };
    }, [bindings, close, dismiss, isOpen, targets]);
}
